import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Box, Typography, Link } from "@mui/material";

import DefaultButton from "../../../components/simple/DefaultButton";
import DefaultBadge from "../../../components/simple/DefaultBadge";
import { Container } from "./style.js";

import RestartAltIcon from "@mui/icons-material/RestartAlt";

dayjs.extend(duration);

const SimMobileCards = ({
  simulations,
  theme,
  page,
  onPageChange,
  sortOrder,
  setDeleteConfirmModal,
  paginationData,
  isLoading,
  deletedId,
  updateFilters,
  hasActiveFilters
}) => {
  const navigate = useNavigate();
  const [accumulatedRows, setAccumulatedRows] = useState([]);
  const observer = useRef(null);

  useEffect(() => {
    if (deletedId) {
      setAccumulatedRows((prevRows) =>
        prevRows.filter((row) => row.id !== deletedId),
      );
    }
  }, [deletedId]);

  // 1. Process the incoming batch of simulations
  const newRows = useMemo(() => {
    return Object.values(simulations || []).map((item) => {
      const createdAt = dayjs(item.created_at);

      const score = item.evaluation?.total_score?.value
        ? (parseFloat(item.evaluation.total_score.value) / 10).toFixed(1) +
          "/10"
        : "N/A";

      // 1. Grab your duration string (assuming it's on item.duration_string, adjust as needed)
      const rawDurationStr = item.duration || "00:00:00.000000";

      // 2. Split the string by the colon
      const [h, m, s] = rawDurationStr.split(":");

      // 3. Feed it into dayjs.duration (rounding the decimal seconds)
      const duration = dayjs.duration({
        hours: parseInt(h, 10),
        minutes: parseInt(m, 10),
        seconds: Math.round(parseFloat(s)), // This rounds 6.462307 down to 6
      });
      const durationDisplay = `${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`;

      return {
        id: item.id,
        scenarioName:
          item.type === "conversation_insight"
            ? item.name || "Unknown"
            : item.name || "Unknown",
        description:
          item.details || "Unknown Simulation Challenge",
        score: score,
        rawScore: item.evaluation?.total_score?.value
          ? item.evaluation?.total_score?.value
          : 0,
        // Exposing the raw ISO string as requested
        created_at: item.created_at,
        completedAtUnix: createdAt.valueOf(),
        // Formatted display values using just created_at
        dateCompleted: createdAt.format("MMM DD, YYYY - HH:mm A"),
        timeCompleted: createdAt.format("HH:mm A"),
        dateCompletedDisplay: `${createdAt.format("MMM DD, YYYY")} - ${createdAt.format("HH:mm")}`,
        duration:
          item.type === "conversation_insight" ? "N/A" : durationDisplay,
        type: item.type,
        durationMs: duration.asMilliseconds(),
      };
    });
  }, [simulations]);

  const displayRows = useMemo(() => {
    // Safety check 5: Fallback to empty array before spreading
    const safeAccumulated = accumulatedRows || [];

    return [...safeAccumulated].sort((a, b) => {
      switch (sortOrder) {
        case "score":
          return b.rawScore - a.rawScore;
        case "completed_asc":
          return a.completedAtUnix - b.completedAtUnix;
        case "completed_desc":
          return b.completedAtUnix - a.completedAtUnix;
        case "sim_duration":
          return b.durationMs - a.durationMs;
        default:
          return 0;
      }
    });
  }, [accumulatedRows, sortOrder]);

  // 2. Accumulate rows (so standard pagination acts like infinite scroll)
  useEffect(() => {
    if (page === 0) {
      // If we are on page 0 (initial load or filters reset), reset the list
      setAccumulatedRows(newRows);
    } else {
      // Otherwise, append new rows. We deduplicate by ID just in case React 18
      // strict mode double-invokes the effect.
      setAccumulatedRows((prev) => {
        const uniqueNewRows = newRows.filter(
          (newRow) => !prev.some((prevRow) => prevRow.id === newRow.id),
        );
        return [...prev, ...uniqueNewRows];
      });
    }
  }, [newRows, page]);

  // 3. Set up the Intersection Observer on the last element
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return; // Don't trigger if already fetching
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && paginationData?.has_next) {
          onPageChange(page + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, paginationData?.has_next, page, onPageChange],
  );

  // 4. Smooth scroll to top handler
  const scrollToTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container id="sim-mobile-card-container">
      {displayRows.length > 0 ? (
        <ul>
          {displayRows.map((sim, i) => {
            const isLastElement = displayRows.length === i + 1;

            return (
              <li
                key={sim.id}
                ref={isLastElement ? lastElementRef : null}
                onClick={() => navigate(`/dashboard/feedback/${sim.id}`)}
              >
                <h3>{sim.scenarioName}</h3>

                {sim.type !== "conversation_insight" && (
                  <p>{sim.description}</p>
                )}

                {sim.type === "conversation_insight" ? (
                  <div className="convo-insights-badge">
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 11 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.5 7L2.96237 8.55685C2.74789 8.77401 2.64065 8.88259 2.54848 8.89025C2.46851 8.89689 2.39021 8.86474 2.33798 8.80382C2.27778 8.7336 2.27778 8.58099 2.27778 8.27577V7.49581C2.27778 7.22198 2.05354 7.02385 1.7826 6.98416C1.12687 6.88811 0.611888 6.37313 0.515838 5.7174C0.5 5.60928 0.5 5.48026 0.5 5.22222V2.9C0.5 2.05992 0.5 1.63988 0.66349 1.31901C0.8073 1.03677 1.03677 0.8073 1.31901 0.66349C1.63988 0.5 2.05992 0.5 2.9 0.5H6.6C7.44008 0.5 7.86012 0.5 8.18099 0.66349C8.46323 0.8073 8.6927 1.03677 8.83651 1.31901C9 1.63988 9 2.05992 9 2.9V5M9 10.5L7.9118 9.74344C7.75883 9.63709 7.68234 9.58391 7.5991 9.54621C7.52522 9.51275 7.44755 9.48841 7.36779 9.47371C7.27792 9.45714 7.18477 9.45714 6.99846 9.45714H6.1C5.53995 9.45714 5.25992 9.45714 5.04601 9.34815C4.85785 9.25228 4.70487 9.0993 4.60899 8.91113C4.5 8.69722 4.5 8.4172 4.5 7.85714V6.6C4.5 6.03995 4.5 5.75992 4.60899 5.54601C4.70487 5.35785 4.85785 5.20487 5.04601 5.10899C5.25992 5 5.53995 5 6.1 5H8.9C9.46005 5 9.74008 5 9.95399 5.10899C10.1422 5.20487 10.2951 5.35785 10.391 5.54601C10.5 5.75992 10.5 6.03995 10.5 6.6V7.95714C10.5 8.42308 10.5 8.65606 10.4239 8.83983C10.3224 9.08485 10.1277 9.27953 9.88268 9.38102C9.69891 9.45714 9.46594 9.45714 9 9.45714V10.5Z"
                        stroke="#FEFEFE"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Conversation Insight
                  </div>
                ) : (
                  <div className="sim-challenge-badge">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 10.5V9.5C11 8.56808 10.3626 7.78503 9.5 7.56301M7.75 1.64538C8.48296 1.94207 9 2.66066 9 3.5C9 4.33934 8.48296 5.05793 7.75 5.35462M8.5 10.5C8.5 9.56812 8.5 9.10218 8.34776 8.73463C8.14477 8.24458 7.75542 7.85523 7.26537 7.65224C6.89782 7.5 6.43188 7.5 5.5 7.5H4C3.06812 7.5 2.60218 7.5 2.23463 7.65224C1.74458 7.85523 1.35523 8.24458 1.15224 8.73463C1 9.10218 1 9.56812 1 10.5M6.75 3.5C6.75 4.60457 5.85457 5.5 4.75 5.5C3.64543 5.5 2.75 4.60457 2.75 3.5C2.75 2.39543 3.64543 1.5 4.75 1.5C5.85457 1.5 6.75 2.39543 6.75 3.5Z"
                        stroke="#030039"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Simulation Challenge
                  </div>
                )}

                <ol>
                  <li>
                    <strong>Score</strong>
                    {sim.score !== "N/A" ? (
                      <DefaultBadge
                        className="hover-badge"
                        variant="pillTransparent"
                        borderColor={theme.palette.border.brand}
                        size="small"
                      >
                        {sim.score}
                      </DefaultBadge>
                    ) : (
                      <DefaultBadge size="small">N/A</DefaultBadge>
                    )}
                  </li>
                  <li>
                    <strong>Duration</strong> {sim.duration}
                  </li>
                  <li>
                    <strong>Date</strong> {sim.dateCompleted}
                  </li>
                  <li>
                    <strong>Time</strong> {sim.timeCompleted}
                  </li>
                </ol>
                <DefaultButton
                  className="delete-sim-btn"
                  backgroundColor="transparent"
                  textColor={theme.palette.text.primary}
                  borderColor={theme.palette.border?.brand}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmModal({ show: true, id: sim.id });
                  }}
                >
                  <svg
                    width="17"
                    height="19"
                    viewBox="0 0 17 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.6667 4.16683V3.50016C11.6667 2.56674 11.6667 2.10003 11.485 1.74351C11.3252 1.42991 11.0703 1.17494 10.7567 1.01515C10.4001 0.833496 9.93342 0.833496 9 0.833496H7.66667C6.73325 0.833496 6.26654 0.833496 5.91002 1.01515C5.59641 1.17494 5.34145 1.42991 5.18166 1.74351C5 2.10003 5 2.56674 5 3.50016V4.16683M6.66667 8.75016V12.9168M10 8.75016V12.9168M0.833336 4.16683H15.8333M14.1667 4.16683V13.5002C14.1667 14.9003 14.1667 15.6004 13.8942 16.1351C13.6545 16.6055 13.2721 16.988 12.8016 17.2277C12.2669 17.5002 11.5668 17.5002 10.1667 17.5002H6.5C5.09987 17.5002 4.39981 17.5002 3.86503 17.2277C3.39462 16.988 3.01217 16.6055 2.77249 16.1351C2.5 15.6004 2.5 14.9003 2.5 13.5002V4.16683"
                      stroke="#FF6467"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Delete Simulation
                </DefaultButton>
              </li>
            );
          })}
        </ul>
      ) : (
        !isLoading && (
          <div className="no-results">
          {hasActiveFilters ? (
            <>
              <strong>No results matching your search criteria</strong>
              <button
                onClick={() =>
                  updateFilters({ date_range_start: "", date_range_end: "", sim_types: "" })
                }
              >
                Reset Filters <RestartAltIcon />
              </button>
            </>
          ) : (
            <>No simulations to show</>
          )}
          </div>
        )
      )}

      {/* Loader UI */}
      {isLoading && page > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 3,
            gap: "15px",
          }}
        >
          <CircularProgress size={20} sx={{ color: "#962EFF" }} />
          <Typography sx={{ fontSize: "14px" }}>Loading more...</Typography>
        </Box>
      )}

      {/* End of Results UI */}
      {!isLoading &&
        paginationData &&
        !paginationData.has_next &&
        accumulatedRows.length > 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
                fontSize: "14px",
                mb: 5,
              }}
            >
              No more simulations to show
            </Typography>
            <Link
              href="#"
              onClick={scrollToTop}
              sx={{
                color: "rgb(1, 236, 255)",
                textDecoration: "none",
                fontSize: "14px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Back to top &uarr;
            </Link>
          </Box>
        )}
    </Container>
  );
};

export default SimMobileCards;
